import type {
  CreateLessonInput,
  LessonDetailResponse,
  LessonResponse,
  UpdateLessonInput,
} from '@lms/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { PaginatedResponse } from '@/lib/api'
import { api } from '@/lib/api'

interface LessonFilters {
  page?: string
  limit?: string
  search?: string
  courseId?: string
}

export function useLessons(filters?: LessonFilters) {
  return useQuery({
    queryKey: ['lessons', filters],
    queryFn: () =>
      api.getFull<PaginatedResponse<LessonResponse>>('/api/lessons', {
        limit: '100',
        ...filters,
      }),
  })
}

export function useLesson(id: string) {
  return useQuery({
    queryKey: ['lessons', id],
    queryFn: () => api.get<LessonDetailResponse>(`/api/lessons/${id}`),
    enabled: !!id,
  })
}

export function useLessonVideoUrl(id: string) {
  return useQuery({
    queryKey: ['lessons', id, 'video-url'],
    queryFn: () => api.get<{ url: string }>(`/api/lessons/${id}/video/secure-url`),
    enabled: !!id,
    staleTime: 1000 * 60 * 30,
  })
}

export function useCreateLesson() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateLessonInput & { tagId?: string }) =>
      api.post<LessonResponse>('/api/lessons', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lessons'] })
      qc.invalidateQueries({ queryKey: ['courses'] })
    },
  })
}

export function useUpdateLesson() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLessonInput & { tagId?: string } }) =>
      api.patch<LessonResponse>(`/api/lessons/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lessons'] }),
  })
}

export function useDeleteLesson() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/lessons/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lessons'] })
      qc.invalidateQueries({ queryKey: ['courses'] })
    },
  })
}
